// TODO: Cache, XSS
import {
  getProductsDetail,
  getProductsDetailByHandle,
  getRedirects,
  getSuggestionSearch,
} from '../api/search.js';
import { getTemplateByIds } from '../api/template.js';
import { CLICK_SUGGESTION_TERM, SUGGESTION_DATA } from '../constants/app';
import { KEYS } from '../constants/keys';
import { dynamicBundleData, getDynamicBundleSettingsByWidgetId } from '../dynamicBundle';
import {
  awaitAnimationElementFinished,
  buildUrlWithLocalization,
  debounce,
  formatCurrency,
  getSearchSettings,
  isBadUrl,
  isMobile,
  preventAllEvents,
  setLocalStorage,
  stripHtml,
} from '../utils';
import {
  getVolumeBundleSettingsByWidgetId,
  volumeBundleData,
} from '../volume-bundle/services/bundle-data';
import { startSession } from '../workers/workers-provider.js';
import { getOnClickRecentSearches, setOnClickRecentSearches } from './recent-search.js';

const BOTTOM_BUNDLE_CLASS = 'boost-sd__suggestion-queries-item--bottom-bundle';
const EMBEDDED_BUNDLE_CLASS = 'boost-sd__suggestion-queries-item--embedded-bundle';
const VOLUME_BUNDLE_CLASS = 'boost-sd__suggestion-queries-item--volume-bundle';
const SELECTED_CLASS = 'boost-sd__suggestion-queries-item-selected';

let keyboardNavIndex = -1;
let currentSearch = '';

/**
 * Handles the instant search widget functionality
 * @type {import('./index').handleInstantSearchWidget}
 */
export const handleInstantSearchWidget = async (context) => {
  const [getInstantSearch] = context.useContextState('instant-search', {});
  const renderAsyncProcess = {
    promise: null,
    resolver: null,
    rejecter: null,
  };

  renderAsyncProcess.promise = new Promise((resolve, reject) => {
    renderAsyncProcess.resolver = resolve;
    renderAsyncProcess.rejecter = reject;
  });

  context.render(
    async function renderISW() {
      const instantSearch = getInstantSearch();
      if (instantSearch && context.templateISW) {
        // Render data
        const translations = context?.app?.translation || {};

        const { send } = startSession('liquid-renderer');
        const { html } = await send({
          template: context.templateISW,
          data: {
            ...instantSearch,
            translations,
            locale: (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, ''),
          },
          options: {
            removeWhitespace: true,
          },
        });

        const instantSearchDom = context.document.querySelector(
          '.boost-sd__instant-search-container'
        );

        if (instantSearchDom) {
          // Already has instant search html, update result to html
          replaceHTML(context, html);
        } else {
          // Create default html for instant search
          context.document.innerHTML = html;
        }

        const suggestions = context.document.querySelectorAll(
          '.boost-sd__suggestion-queries-list li.boost-sd__suggestion-queries-item--suggestion'
        );

        suggestions.forEach((suggestion) => {
          suggestion.addEventListener('click', (e) => {
            setLocalStorage(CLICK_SUGGESTION_TERM, suggestion.getAttribute('data-title'));
          });
        });

        //Generate newId for customize
        const newId = window.boostWidgetIntegration.generateUuid();
        context.state.latestInstantSearchRequest = newId;

        initInstantSearchWidgetEvents(context);
      }
    },
    ['instant-search']
  );

  initInstantSearchWidget(context);
};

/**
 * Initializes instant search widget events
 * @type {import('./index').initInstantSearchWidgetEvents}
 */
const initInstantSearchWidgetEvents = (context) => {
  const urlParams = new URLSearchParams(window.location.search);
  const query = urlParams.get('q');

  const inputs = document?.querySelectorAll(`input[name="q"]:not([data-disable-instant-search])`);

  inputs.forEach((input, index) => {
    input.id = `boost-sd__search-widget-init-input-${index}`;
    input.classList.add('boost-sd__search-widget-init-input');
    input.setAttribute('aria-expanded', 'false');

    // disabled autocomplete of chrome
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('data-search-box', `boost-sd__search-widget-init-input-${index}`);
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('aria-owns', 'boost-sd__search-widget-init-wrapper');
    input.setAttribute('aria-controls', 'boost-sd__search-widget-init-wrapper');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute(
      'aria-label',
      context.app?.translation?.suggestion?.searchBoxPlaceholder || 'Search'
    );
    input.setAttribute('maxlength', '150');
    input.setAttribute(
      'placeholder',
      context.app?.translation?.suggestion?.searchBoxPlaceholder || 'Search'
    );
    input.setAttribute('value', query ? stripHtml(query) : '');

    // Improve ADA: set label[for] for each input in each layout
    const wrapper = input.closest(
      '.boost-sd__instant-search-container-mobile, .boost-sd__instant-search-container-desktop'
    );
    if (wrapper) {
      const label = wrapper.querySelector('.boost-sd__search-bar-label');
      if (label) {
        label.setAttribute('for', input.id);
      }
    }

    function ensureMobileWrapperVisible() {
      const wrapper = document.querySelector('.boost-sd__search-bar-wrapper--mobile');
      if (wrapper && wrapper.classList.contains('boost-sd__g-hide')) {
        wrapper.classList.remove('boost-sd__g-hide');
      }
    }

    // Bind event
    if (!input.dataset.eventBound) {
      input.addEventListener('input', (e) => {
        handleSearchInputChange(context, e, input);
        searchInputListener(context, e, input);
        ensureMobileWrapperVisible();
      });

      input.addEventListener('click', async (e) => {
        searchInputListener(context, e, input);
        ensureMobileWrapperVisible();
      });

      input.addEventListener('focusin', (e) => {
        ensureMobileWrapperVisible();
        searchInputListener(context, e, input);
      });

      if (!window.boostSdDisableISWKeyDown) {
        input.addEventListener('keydown', (e) => {
          searchInputKeydownListener(context, e, input);
        });
      }

      // detect button: search and reset
      const thisSearchForm = input.closest('form');
      if (thisSearchForm) {
        // Button search
        const thisSearchButton = thisSearchForm?.querySelector('[type="submit"], .search__button');
        if (thisSearchButton) {
          thisSearchButton.addEventListener('click', (e) => {
            searchButtonListener(context, e, input);
          });

          // set keyboardActivatedSearch support wcag for redirect search page
          thisSearchButton.addEventListener('keydown', (e) => {
            if (KEYS.includes(e.key)) {
              thisSearchButton.classList.add('keyboard-activated');
              sessionStorage.setItem('keyboardActivatedSearch', 'true');
            }
          });
        }
        // Button reset
        const thisResetButton = thisSearchForm?.querySelector(
          '[type="reset"], .reset__button, .boost-sd__button-text-x--clear'
        );
        if (thisResetButton) {
          if (query) {
            thisResetButton.classList.remove('boost-sd__g-hide');
          }
          const [, setInstantSearch] = context.useContextState('instant-search', {});
          const { defaultData } = context;

          thisResetButton.addEventListener('click', (e) => {
            e.preventDefault();
            resetButtonListener(input, thisResetButton, setInstantSearch, defaultData);
          });
        }

        // Button close - available on full-width
        const closeButton = thisSearchForm?.querySelector('.boost-sd__button-text-x--close');
        if (closeButton) {
          closeButton.addEventListener('click', function () {
            onCloseSuggestion(context);
          });
        }
      }

      if (document.activeElement === input) {
        searchInputListener(context, null, input);
      }

      input.dataset.eventBound = 'true';
    }
  });

  // Bind event document
  // Event click on document to close instant search
  const debounceDocumentClick = debounce((e) => {
    const isMobileValue = isMobile(context.app.generalSettings?.isTabletPortraitMax);
    const suggestionSelector = context.document?.querySelector(
      `.boost-sd__instant-search-container-${isMobileValue ? 'mobile' : 'desktop'} > div`
    );

    const inputId = suggestionSelector?.getAttribute('aria-controls');
    const input = document.getElementById(inputId);

    if (isSuggestionOpening(suggestionSelector)) {
      // click Outside check close suggestion results
      clickOutsideSuggestion(context, suggestionSelector, input, e);
    }
  }, 100);

  // Bind the event click
  document.addEventListener('click', debounceDocumentClick, true);

  // Event resize the window => change between mobile/desktop mode and re-calculate the position
  const debounceResize = debounce(async () => {
    const [getIsMobile, setIsMobile] = context.useContextState('is-mobile', false);
    const isMobileValue = isMobile(context.app.generalSettings?.isTabletPortraitMax);

    if (getIsMobile() !== isMobileValue) {
      // Responsive to another style desktop <-> mobile
      // Remove all selected
      keyboardNavIndex = -1;
      const input = document?.querySelector(
        "input[aria-activedescendant='boost-sd__suggestion-queries-item-selected']"
      );
      removeCurrentSelected(context, input);
      // Close ISW
      onCloseSuggestion(context);
      // tips: trigger click, hide search of theme
      const suggestionSelector = context.document?.querySelector(
        `.boost-sd__instant-search-container-${isMobileValue ? 'mobile' : 'desktop'} > div`
      );
      if (suggestionSelector) {
        suggestionSelector.click();
      }

      // Update state
      setIsMobile(isMobileValue);
    } else {
      // Responsive to the same style desktop/mobile
      // Recalculate position - Apply for non - fullwidth style
      const suggestionSelector = context.document?.querySelector(
        `.boost-sd__instant-search-container-${
          isMobileValue ? 'mobile' : 'desktop'
        } .boost-sd__search-widget-init-wrapper`
      );
      if (suggestionSelector && isSuggestionOpening(suggestionSelector)) {
        const inputId = suggestionSelector?.getAttribute('aria-controls');
        const input = document.getElementById(inputId);
        if (input) {
          // await modal search display
          await awaitAnimationElementFinished(input, 100, 1000);

          calcPositionSuggestionResult(context, input);
        }
      }
    }
  }, 100);

  // Bind the resize event
  window.addEventListener('resize', debounceResize);

  document.body.classList.add('boost-sd__search-widget-init-enabled'); // Code from old project for enabled ISW css
};

/**
 * Initializes instant search widget
 * @type {import('./index').initInstantSearchWidget}
 */
const initInstantSearchWidget = async (context) => {
  const [getInstantSearch, setInstantSearch] = context.useContextState('instant-search', {});
  const [, setIsMobile] = context.useContextState('is-mobile', false);

  context.templateISW = await requestInstantSearchWidgetTemplate(context); // Get template for ISW

  // Build default data
  context.defaultData = await buildDefaultDataSuggestion(context);

  // Update responsive state
  setIsMobile(isMobile(context.app.generalSettings?.isTabletPortraitMax));

  // Set default instant search state
  const urlParams = new URLSearchParams(window.location.search);
  const query = urlParams.get('q');
  if (query) {
    // Get data for instant search
    // Loading
    const instantSearch = getInstantSearch();
    const { isLoading = false } = instantSearch;
    if (!isLoading) {
      setInstantSearch({ ...context.defaultData, isLoading: true });
    }

    // Get data
    const data = await requestSuggestion(context, query);

    setInstantSearch({ ...context.defaultData, ...data, isLoading: false });
  } else {
    setInstantSearch(context.defaultData);
  }

  initInstantSearchWidgetEvents(context);
};

// ------------COMMON FUNCTIONS------------
/**
 * Generates search page href with given term and extra parameters
 * @type {import('./index').generateSearchPageHref}
 */
const generateSearchPageHref = (term = '', extraParam = '') => {
  const extraSearchParam = extraParam ? `&${extraParam.replace(/&/g, 'AND')}` : '';

  return buildUrlWithLocalization(
    `search?q=${encodeURIComponent(term)}${extraSearchParam}`
  ).replace(/%20/g, '+');
};

/**
 * Checks if current state is fullwidth
 * @type {import('./index').initInstantSearchWidgetEvents}
 */
const isStyleFullWidth = (context) => {
  const isMobileValue = isMobile(context.app.generalSettings?.isTabletPortraitMax);
  const { suggestionStyle, suggestionMobileStyle } = getSearchSettings(context);
  return (
    (isMobileValue && suggestionMobileStyle === 'style1') ||
    (!isMobileValue && suggestionStyle === 'style3')
  );
};

// ---------------HANDLE DATA--------------

/**
 * Requests instant search widget template
 * @type {import('./index').requestInstantSearchWidgetTemplate}
 */
// Get ISW template
export const requestInstantSearchWidgetTemplate = async (context) => {
  const { templates = {} } = await getTemplateByIds(
    context,
    context.app.templateMetadata?.themeNameLib,
    [{ name: 'instantSearchWidget', id: context.app.templateMetadata?.instantSearchWidget }]
  );

  return templates[context.app.templateMetadata?.instantSearchWidget] || '';
};

/**
 * Builds default data for suggestion
 * @type {import('./index').buildDefaultDataSuggestion}
 */
const buildDefaultDataSuggestion = async (context) => {
  let defaultData = {
    // Data settings
    suggestionMobileStyle: 'style1', //Hard code,
    suggestionMobileProductItemType: 'grid',
    redirects: context.redirects,
    // Data state
    isLoading: false,
    money_format: context?.app?.shop?.money_format,
    money_format_with_currency: context?.app?.shop?.money_format_with_currency,
    // Data from API data
    all_empty: true,
    collections: [],
    correct_content_query: '',
    correct_query: '',
    did_you_mean: [],
    pages: [],
    query: '',
    suggestions: [],
    total_product: 0,
  };

  const searchSettings = getSearchSettings(context);
  // Get redirect
  context.redirects = await getRedirects(context);
  defaultData.redirects = context.redirects;

  let suggestProductsOnClick = [];
  // Get suggestion product when search box on click
  if (searchSettings?.searchBoxOnclick?.productSuggestion?.status) {
    suggestProductsOnClick = await getSuggestProducts(context);
  }
  defaultData.products = suggestProductsOnClick || [];

  // Recent search
  const recentSearches = getOnClickRecentSearches(
    searchSettings?.searchBoxOnclick?.recentSearch?.number || 10
  );
  defaultData.recentSearches = recentSearches;

  // Data from search setting
  defaultData = {
    ...defaultData,
    searchTermSuggestionList: searchSettings.searchBoxOnclick?.searchTermSuggestion?.data || [],
    searchTermSuggestionStatus:
      searchSettings.searchBoxOnclick?.searchTermSuggestion?.status || false,
    searchTermNoResultList: searchSettings.suggestionNoResult?.search_terms?.data || [],
    searchTermNoResultStatus: searchSettings.suggestionNoResult?.search_terms?.status || false,
    searchTermSuggestionLabel: searchSettings.searchBoxOnclick?.searchTermSuggestion?.label || '',
    searchTermNoResultLabel: searchSettings.suggestionNoResult?.search_terms?.label,
    suggestionBlocksList: searchSettings.suggestionBlocks,
  };

  return defaultData;
};

/**
 * Gets suggestion products for search box on click
 * @type {import('./index').getSuggestProducts}
 */
const getSuggestProducts = async (context) => {
  const searchSettings = getSearchSettings(context);
  const productIds = searchSettings?.searchBoxOnclick?.productSuggestion?.data;
  if (productIds.length === 0) return [];

  let result = await getProductsDetail(context, productIds);
  result = transformProductPrice(context, result);
  return result;
};

/**
 * Gets suggestion results
 * @type {import('./index').requestSuggestion}
 */
const requestSuggestion = async (context, query) => {
  let data = await context.handler(getSuggestionSearch, 'getSuggestionSearch')(context, query);
  const searchSettings = getSearchSettings(context);

  // improve logic, if allEmpty = true -> check call api trending product
  if (data.all_empty) {
    // get most product if enabled
    const _mostProductsSetting = searchSettings.suggestionNoResult?.products || {};
    if (_mostProductsSetting.status && _mostProductsSetting.data.length > 0) {
      let products = await getProductsDetailByHandle(context, _mostProductsSetting.data, query);
      if (products) {
        products = transformProductPrice(context, products);
        data = { ...data, products };
      }
    }
  } else {
    const suggestionBlocks = searchSettings.suggestionBlocks;
    const disabledBlocks = {
      products: false,
      suggestions: false,
      collections: false,
      pages: false,
    };

    suggestionBlocks.forEach((block) => {
      if (block.status !== 'active') {
        disabledBlocks[block.type] = true;
      }
    });

    const hasProducts = data?.products?.length > 0 && !disabledBlocks.products;
    const hasSuggestions = data?.suggestions?.length > 0 && !disabledBlocks.suggestions;
    const hasCollections = data?.collections?.length > 0 && !disabledBlocks.collections;
    const hasPages = data?.pages?.length > 0 && !disabledBlocks.pages;

    const allEmpty = !hasProducts && !hasSuggestions && !hasCollections && !hasPages;

    if (allEmpty) {
      const _mostProductsSetting = searchSettings.suggestionNoResult?.products || {};
      if (_mostProductsSetting.status && _mostProductsSetting.data.length > 0) {
        const products = await getProductsDetailByHandle(context, _mostProductsSetting.data, query);
        if (products) {
          data.products = transformProductPrice(context, products);
        }
      }
    } else {
      data.products = transformProductPrice(context, data.products);
    }

    setLocalStorage(SUGGESTION_DATA, { query, id: data.meta.rid, suggestions: data.suggestions });
  }

  const targetBundle = document?.getElementById('bc-instant-search');

  if (data?.bundles?.length > 0) {
    data.widgetDesignSettingsMap = {};

    for (const bundle of data.bundles) {
      if (bundle.bundle_type === 'volume') {
        data.bundle = await volumeBundleData(context, bundle, {
          optionData: data.products,
          target: targetBundle,
          position: 'isw',
        });
        data.widgetDesignSettingsMap[bundle.widgetId] = {
          ...getVolumeBundleSettingsByWidgetId(context, bundle?.widgetId)?.widgetDesignSettings,
        };
      } else {
        data.bundle = await dynamicBundleData(context, bundle);
        data.widgetDesignSettingsMap[bundle.widgetId] = {
          ...getDynamicBundleSettingsByWidgetId(context, bundle?.widgetId)?.widgetDesignSettings,
        };
      }
    }

    data.widgetDesignSettings = data.widgetDesignSettingsMap[data.bundles[0]?.widgetId] || {};
  }

  return data;
};

/**
 * Gets suggestion results with debounce
 * @type {import('./index').requestSuggestionDebounce}
 */
const requestSuggestionDebounce = debounce(async (context, query) => {
  const [getInstantSearch, setInstantSearch] = context.useContextState('instant-search', {});

  if (query) {
    // Loading
    const instantSearch = getInstantSearch();
    const { isLoading = false } = instantSearch;
    if (!isLoading) {
      setInstantSearch({ ...context.defaultData, isLoading: true });
    }
    // Get data
    const data = await requestSuggestion(context, query);
    setInstantSearch({ ...context.defaultData, ...data, isLoading: false });
  } else {
    setInstantSearch(context.defaultData);
  }
}, 300);

/**
 * Replaces HTML from template to DOM
 * @type {import('./index').replaceHTML}
 */
const replaceHTML = (context, htmlString) => {
  const instantSearchDom = context.document?.querySelector('.boost-sd__instant-search-container');
  if (instantSearchDom) {
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlString, 'text/html');

    // Replace mobile block
    const mobileSelector =
      '.boost-sd__instant-search-container-mobile .boost-sd__instant-search-results';
    const suggestionSelectorMobile = instantSearchDom?.querySelector(mobileSelector);
    const newSuggestionSelectorMobile = htmlDoc?.querySelector(mobileSelector);

    if (suggestionSelectorMobile && newSuggestionSelectorMobile) {
      suggestionSelectorMobile.parentNode.replaceChild(
        newSuggestionSelectorMobile,
        suggestionSelectorMobile
      );
    }

    // Replace desktop block
    const desktopSelector =
      '.boost-sd__instant-search-container-desktop .boost-sd__instant-search-results';
    const suggestionSelectorDesktop = instantSearchDom?.querySelector(desktopSelector);
    const newSuggestionSelectorDesktop = htmlDoc?.querySelector(desktopSelector);

    if (suggestionSelectorDesktop && newSuggestionSelectorDesktop) {
      suggestionSelectorDesktop.parentNode.replaceChild(
        newSuggestionSelectorDesktop,
        suggestionSelectorDesktop
      );
    }
  }
};

// ------------EVENT FUNCTIONS------------
/**
 * Handles search input change
 * @type {import('./index').handleSearchInputChange}
 */
export const handleSearchInputChange = (context, e, input) => {
  e.preventDefault();
  keyboardNavIndex = -1;
  removeCurrentSelected(context, input);
  currentSearch = e.target.value;

  // Button reset
  const thisSearchForm = input.closest('form');
  if (thisSearchForm) {
    const thisResetButton = thisSearchForm?.querySelector(
      '[type="reset"], .reset__button, .boost-sd__button-text-x--clear'
    );
    if (thisResetButton) {
      if (currentSearch) {
        // Show button reset
        thisResetButton.classList.remove('boost-sd__g-hide');
      } else {
        // Hide button reset
        thisResetButton.classList.add('boost-sd__g-hide');
      }
    }
  }

  const [, setInstantSearch] = context.useContextState('instant-search', {});

  // Get data for instant search based on the current input value
  if (currentSearch === '') {
    setInstantSearch(context.defaultData);
  } else {
    requestSuggestionDebounce(context, currentSearch);
  }

  // Sync all inputs
  const inputs = document?.querySelectorAll(`input[name="q"]:not([data-disable-instant-search])`);
  inputs.forEach((syncInput) => {
    if (syncInput !== e.target) {
      syncInput.value = currentSearch;
    }
  });
};

/**
 * Handles search input click/focus
 * @type {import('./index').searchInputListener}
 */
export const searchInputListener = async (context, e, input) => {
  // check current input is not input of owner then check style
  const isOwnerInput = input.closest('.boost-sd__instant-search-container');
  if (!isOwnerInput && !isStyleFullWidth(context)) {
    // await modal search display
    await awaitAnimationElementFinished(input, 100, 1000);
  }

  calcPositionSuggestionResult(context, input);
  onOpenSuggestion(context, input);
};

/**
 * Handles search input keydown
 * @type {import('./index').searchInputKeydownListener}
 */
export const searchInputKeydownListener = (context, e, input) => {
  const isMobileValue = isMobile(context.app.generalSettings?.isTabletPortraitMax);
  const suggestionSelector = context.document?.querySelector(
    `.boost-sd__instant-search-container-${isMobileValue ? 'mobile' : 'desktop'} > div`
  );
  if (!e || !e.key) return;

  switch (e.key) {
    // Enter key
    case 'Enter': {
      preventAllEvents(e);

      const _currentValue = stripHtml(e.target.value.trim());
      setOnClickRecentSearches(_currentValue);

      const selector = `
        .${EMBEDDED_BUNDLE_CLASS}.${SELECTED_CLASS},
        .${VOLUME_BUNDLE_CLASS}.${SELECTED_CLASS},
        .${BOTTOM_BUNDLE_CLASS}.${SELECTED_CLASS}
      `;

      const bundleItem = suggestionSelector?.querySelector(selector);

      const clearSearchForm = suggestionSelector?.querySelector(
        ':not(.boost-sd__g-hide)[data-group="clear-search-form"]'
      );
      const closeSearchForm = suggestionSelector?.querySelector(
        '.boost-sd__button-text-x--close[data-group="close-search-form"]'
      );
      const searchElement = document?.querySelector(`
        .logo-area__right .show-search-link,
        .header__icon--search,
        .header__icon-wrapper,
        .js-search-sidebar-link,
        .js-search-header,
        .header__secondary-nav a[href="/search"],
        .site-header__links .search-button,
        .header-navs .js-search-trigger,
        .header--search,
        .navlink--search,
        .js-modal-open-search-modal,
        .slideout__trigger-search
      `);

      const searchForm = input?.closest('form');
      const submitElement = document?.querySelector('.boost-sd__search-form-submit-btn');

      if (bundleItem) {
        const dynamicWidgetId = bundleItem?.querySelector(
          '.boost-sd__recommendation--dynamic, .boost-sd__product-bundle'
        )?.dataset?.widgetId;
        if (dynamicWidgetId) {
          window.dispatchEvent(
            new CustomEvent('boost-sd-open-dynamic-bundle', {
              detail: {
                widgetId: dynamicWidgetId,
                context: context,
              },
            })
          );
        }

        const volumeWidgetId = bundleItem?.querySelector(
          '.boost-sd__recommendation--volume, .boost-sd__volume-bundle-product-item'
        )?.dataset?.widgetId;
        if (volumeWidgetId) {
          window.dispatchEvent(
            new CustomEvent('boost-sd-open-volume-bundle', {
              detail: {
                widgetId: volumeWidgetId,
                context: context,
              },
            })
          );
        }
      } else if (shouldClearSearchForm(clearSearchForm, closeSearchForm)) {
        clearSearchForm.dispatchEvent(new Event('click'));
      } else if (shouldCloseSearchForm(closeSearchForm, clearSearchForm)) {
        closeSearchForm.dispatchEvent(new Event('click'));
        document.body.click();
        focusOnRelevantElement(submitElement, searchElement, searchForm);
      } else {
        handleSearchRedirect(_currentValue, suggestionSelector);
      }

      break;
    }

    // current focus input, and Tab -> move to other element -> close
    case 'Tab': {
      onCloseSuggestion(context, input);
      break;
    }
    // esc key => close
    case 'Escape': {
      return onCloseSuggestion(context, input);
    }
    //Arrow/navigation key up, down, left, right
    case 'ArrowUp':
    case 'ArrowDown':
    case 'ArrowLeft':
    case 'ArrowRight': {
      if (window.boostSdDisableISWArrow) return;

      if (isSuggestionOpening(suggestionSelector)) {
        onKeyboardNavigation(context, input, suggestionSelector, e);
      }
      break;
    }
  }

  // Helper Functions
  function shouldClearSearchForm(clearSearchForm, closeSearchForm) {
    return clearSearchForm && (closeSearchForm || !closeSearchForm) && keyboardNavIndex === 0;
  }

  function shouldCloseSearchForm(closeSearchForm, clearSearchForm) {
    return (
      closeSearchForm &&
      ((clearSearchForm && keyboardNavIndex === 1) || (!clearSearchForm && keyboardNavIndex === 0))
    );
  }

  function focusOnRelevantElement(submitElement, searchElement, searchForm) {
    if (submitElement) {
      submitElement.focus();
    } else if (searchElement) {
      searchElement.focus();
    } else if (searchForm) {
      const submitButton = searchForm?.querySelector('button[type="submit"], input[type="submit"]');
      if (submitButton) {
        submitButton.focus();
      }
    } else {
      handleFallbackFocus();
    }
  }

  function handleFallbackFocus() {
    window.scrollTo({
      top: 10,
      behavior: 'smooth',
    });
    const skipToContentLink = Array.from(document?.querySelectorAll('a')).find((link) =>
      link.textContent.includes('Skip to content')
    );
    if (skipToContentLink) {
      skipToContentLink.focus();
    }
  }

  function handleSearchRedirect(value, suggestionSelector) {
    if (value.length > 0) {
      const redirects = context.redirects;
      if (redirects && redirects[value.toLowerCase()]) {
        window.location.href = redirects[value.toLowerCase()];
      } else {
        const linkSelected = selectItemCurrentSelected(suggestionSelector);
        window.location.href = linkSelected || generateSearchPageHref(value);
      }
    } else {
      window.location.href = generateSearchPageHref();
    }
  }
};

/**
 *  Handles search button click
 * @type {import('./index').searchButtonListener}
 */
export const searchButtonListener = (context, e, input) => {
  e.preventDefault();

  // save recent search when click button search
  setOnClickRecentSearches(stripHtml(input.value.trim()));

  // check redirect when click button search
  const redirects = context?.redirects;

  if (redirects && redirects[input.value?.toLowerCase()]) {
    preventAllEvents(e);
    window.location.href = redirects[input.value?.toLowerCase()];
    return;
  }

  window.location.href = generateSearchPageHref(input.value);
};

/**
 * Handles reset button click
 * @type {import('./index').resetButtonListener}
 */
export const resetButtonListener = (input, thisResetButton) => {
  // Clear data on input
  input.value = '';
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.focus();

  // toggle reset button
  thisResetButton.classList.add('boost-sd__g-hide');
};

/**
 * Calculates position of current suggestion result modal
 * @type {import('./index').calcPositionSuggestionResult}
 */
const calcPositionSuggestionResult = (context, input) => {
  // Batch all DOM reads first
  const isMobileValue = isMobile(context.app.generalSettings?.isTabletPortraitMax);
  const selector = `.boost-sd__instant-search-container-${isMobileValue ? 'mobile' : 'desktop'} .boost-sd__search-widget-init-wrapper`;
  const suggestionSelector = context.document?.querySelector(selector);

  if (suggestionSelector) {
    const rect = input.getBoundingClientRect();
    const position = 'absolute';

    // + 2 px => border of input
    // + 4 px: distance from input from input autocomplete to this input
    // window.scrollY: if window has scroll Y
    const scroll = window.scrollY;
    let top = rect.height + rect.top + 2 + 4;
    // - 20: distance from suggestion to bottom of window
    const _maxHeight = window.innerHeight - top - (isStyleFullWidth(context) ? 0 : 20);

    let _positionLeftOrRight = `left: ${rect.left}px`;

    // auto detect suggestion position
    if (window.innerWidth / 2 < rect.left) {
      _positionLeftOrRight = `right: ${window.innerWidth - rect.right}px`;
    }

    top = top + scroll;
    const maxHeightByStyle = calcMaxHeightByStyle(context, _maxHeight);

    // Batch all DOM writes in rAF
    requestAnimationFrame(() => {
      suggestionSelector.style.position = position;
      suggestionSelector.style.top = `${top}px`;
      suggestionSelector.style.zIndex = '999999998';
      const side = _positionLeftOrRight.startsWith('left') ? 'left' : 'right';
      suggestionSelector.style[side] =
        side === 'left' || side === 'right' ? _positionLeftOrRight.split(':')[1].trim() : 'left';

      const thisResults = suggestionSelector.querySelector(
        '.boost-sd__instant-search-autocomplete-results'
      );
      if (thisResults) {
        thisResults.style.maxHeight = `${maxHeightByStyle}px`;
      }
    });

    // Debounced scroll handler (unchanged, but now only updates styles in rAF)
    const debounceScrollPage = debounce(() => {
      requestAnimationFrame(() => {
        const header = document.getElementsByTagName('header');
        const searchForm = document.getElementsByClassName('boost-sd__search-form');
        let _zIndex;
        if (
          header.length > 0 &&
          window.scrollY > 0 &&
          searchForm[0]?.getBoundingClientRect().bottom <=
            header[0].getBoundingClientRect().top + header[0].clientHeight
        ) {
          _zIndex = 2;
        } else {
          _zIndex = 999999998;
        }
        suggestionSelector.style.zIndex = _zIndex;
      });
    }, 10);

    window.addEventListener('scroll', debounceScrollPage);
  }
};

/**
 * Calculate max height by style - except fullwidth
 * @type {import('./index').calcMaxHeightByStyle}
 */
const calcMaxHeightByStyle = (context, maxHeight) => {
  const {
    suggestionStyle,
    suggestionStyle2ProductItemType,
    suggestionMobileStyle,
    suggestionMaxHeight = 0,
  } = getSearchSettings(context);
  let _maxHeight = maxHeight;

  // case current maxHeight >= maxHeightSettings -> chose maxHeightSettings
  if (suggestionMaxHeight && suggestionMaxHeight > 0 && _maxHeight >= suggestionMaxHeight) {
    return suggestionMaxHeight;
  }

  // !suggestionMaxHeight or maxHeight < suggestionMaxHeight
  if (!isMobile(context.app.generalSettings?.isTabletPortraitMax)) {
    if (suggestionStyle === 'style2' && suggestionStyle2ProductItemType === 'list') {
      // 564px
      _maxHeight = Math.min(_maxHeight, 564);
    } else {
      // 640px
      _maxHeight = Math.min(_maxHeight, 640);
    }
  } else {
    if (suggestionMobileStyle === 'style2') {
      _maxHeight = Math.min(_maxHeight, 640);
    }
  }

  return _maxHeight;
};

/**
 * Checks if suggestion is opening
 * @type {import('./index').isSuggestionOpening}
 */
const isSuggestionOpening = (suggestionSelector) => {
  return !suggestionSelector?.classList?.contains('boost-sd__g-hide');
};

/**
 * Open all suggestion
 * @type {import('./index').onOpenSuggestion}
 */
const onOpenSuggestion = (context, input) => {
  const isMobileValue = isMobile(context.app?.generalSettings?.isTabletPortraitMax);
  const selector = `.boost-sd__instant-search-container-${isMobileValue ? 'mobile' : 'desktop'} > div`;
  const suggestionSelector = context.document?.querySelector(selector);
  if (suggestionSelector) {
    if (isStyleFullWidth(context)) {
      // remove some theme have overlay, can't override or click outside not close search modal -> trigger click close this button.
      // Prestige , Supper store, Venture, District (in order)
      const closeSearchThemeSelector = context.app?.generalSettings?.closeSearchThemeSelector;
      const selector = [
        '#Search .Search__Close',
        '.js-drawer-open .page-container',
        '.menu-drawer__modal-close.modal__close',
        ...(closeSearchThemeSelector ? [closeSearchThemeSelector] : []),
      ].join(',');

      // Batch DOM reads
      const closeSearchThemes = Array.from(
        context.document?.querySelectorAll(selector || []) ?? []
      ).filter((el) => el.offsetParent !== null);

      if (closeSearchThemes?.length > 0) {
        requestAnimationFrame(() => {
          closeSearchThemes.forEach((closeSearchTheme) => closeSearchTheme.click());
        });
      }
    }

    if (!isSuggestionOpening(suggestionSelector)) {
      requestAnimationFrame(() => {
        suggestionSelector.classList.remove('boost-sd__g-hide');
        suggestionSelector.setAttribute('aria-controls', input.id);
      });
    }

    if (isStyleFullWidth(context) || !input.classList.contains('boost-sd__search-form-input')) {
      requestAnimationFrame(() => {
        document.body.classList.add('boost-sd__search-opening');
      });
    }

    input.setAttribute('aria-expanded', 'true');
  }

  // Handle full-width
  const searchBarInput = context.document?.querySelector(
    `.boost-sd__instant-search-container-${
      isMobileValue ? 'mobile' : 'desktop'
    } .boost-sd__search-bar-input`
  );

  const preventInfiniteISW = context.app?.generalSettings?.preventInfiniteISW;
  const searchElement = document?.querySelector(
    '.header__icon-wrapper, .header__nav-icon.icon-search'
  );

  if (searchBarInput) {
    if (preventInfiniteISW || searchElement) {
      /**
       * ISSUE: Infinite uncaught errors - Issue occurred after fixing PFSN-43210
       * TARGET: focal, prestige theme
       */
      setTimeout(() => searchBarInput.focus(), 200);
    } else {
      searchBarInput.focus();
    }
    document.body.classList.add('boost-sd__g-no-scroll');
  }
};

/**
 * Close all suggestion
 * @type {import('./index').onCloseSuggestion}
 */
const onCloseSuggestion = (context, input) => {
  const hide = () => {
    const isMobileValue = isMobile(context.app?.generalSettings?.isTabletPortraitMax);
    const selector = `.boost-sd__instant-search-container-${isMobileValue ? 'mobile' : 'desktop'} > div`;
    const suggestionSelector = context.document?.querySelector(selector);
    if (suggestionSelector) {
      requestAnimationFrame(() => {
        suggestionSelector.classList.add('boost-sd__g-hide');
        suggestionSelector.setAttribute('aria-controls', '');
        document.body.classList.remove('boost-sd__g-no-scroll');
        document.body.classList.remove('boost-sd__search-opening');
        if (input) {
          input.setAttribute('aria-expanded', 'false');
        }
      });
    }
  };

  if (document.body.classList.contains('show-search')) {
    // close ISW full-width - Symmetry Theme
    document.body.classList.remove('show-search');
    setTimeout(hide, 200);
    return;
  }

  hide();
};

/**
 * Handle keyboard up-down: select/remove item
 * @type {import('./index').onKeyboardNavigation}
 */
const onKeyboardNavigation = (context, input, suggestionSelector, e) => {
  if (!suggestionSelector) return;
  if (!isSuggestionOpening(suggestionSelector) || !e || !e.key) return;
  const suggestionItems = suggestionSelector?.querySelectorAll(
    '.boost-sd__suggestion-queries-item'
  );

  if (!suggestionItems || suggestionItems.length === 0) return;
  const viewAllProducts = suggestionSelector?.querySelector('[data-group="view-all-products"]');
  const clearSearchForm = suggestionSelector?.querySelector(
    ':not(.boost-sd__g-hide)[data-group="clear-search-form"]'
  );
  const closeSearchForm = suggestionSelector?.querySelector(
    '.boost-sd__button-text-x--close[data-group="close-search-form"]'
  );

  const numberOfItems =
    suggestionItems.length +
    (viewAllProducts ? 1 : 0) +
    (clearSearchForm ? 1 : 0) +
    (closeSearchForm ? 1 : 0);
  let isStopPropagation = false;

  if (e.key.includes('Down') || e.key.includes('Up')) {
    isStopPropagation = true;

    if (e.key.includes('Up')) {
      keyboardNavIndex--;
      if (keyboardNavIndex < -1) {
        keyboardNavIndex = numberOfItems - 1;
      }
    } else if (e.key.includes('Down')) {
      keyboardNavIndex++;
      if (keyboardNavIndex > numberOfItems - 1) {
        keyboardNavIndex = -1;
      }
    }
  }

  input.focus();

  // Define variables for conditions
  const isInputSelected = keyboardNavIndex === -1;
  const isClearSearchFormActive = keyboardNavIndex === 0 && clearSearchForm;
  const isCloseSearchFormActive =
    (keyboardNavIndex === 0 && closeSearchForm) ||
    (keyboardNavIndex === 1 && closeSearchForm && clearSearchForm);
  const isViewAllProductsActive =
    viewAllProducts &&
    ((!clearSearchForm && !closeSearchForm && keyboardNavIndex > suggestionItems.length - 1) ||
      (!clearSearchForm && closeSearchForm && keyboardNavIndex > suggestionItems.length) ||
      (clearSearchForm && closeSearchForm && keyboardNavIndex > suggestionItems.length + 1));

  // Focus elements when navigating by keyboard up/down
  if (isInputSelected) {
    // select input
    if (currentSearch) {
      input.value = currentSearch;
    }
    removeCurrentSelected(context, input);
  } else if (isClearSearchFormActive) {
    removeCurrentSelected(context, input);
    const idActiveDescendantFocus = 'boost-sd__suggestion-queries-item-selected';
    input.setAttribute('aria-activedescendant', idActiveDescendantFocus);

    clearSearchForm.id = idActiveDescendantFocus;
    clearSearchForm.classList.add('boost-sd__suggestion-queries-item-selected');
    clearSearchForm.setAttribute('aria-selected', 'true');
  } else if (isCloseSearchFormActive) {
    removeCurrentSelected(context, input);
    const idActiveDescendantFocus = 'boost-sd__suggestion-queries-item-selected';
    input.setAttribute('aria-activedescendant', idActiveDescendantFocus);
    closeSearchForm.id = idActiveDescendantFocus;
    closeSearchForm.classList.add('boost-sd__suggestion-queries-item-selected');
    closeSearchForm.setAttribute('aria-selected', 'true');
  } else if (isViewAllProductsActive) {
    // view all
    removeCurrentSelected(context, input);
    addItemSelected(viewAllProducts, input);
  } else {
    removeCurrentSelected(context, input);
    suggestionItems.forEach((item, index) => {
      const isItemSelected =
        (!clearSearchForm && !closeSearchForm && keyboardNavIndex === index) ||
        (!clearSearchForm && closeSearchForm && keyboardNavIndex === index + 1) ||
        (clearSearchForm && closeSearchForm && keyboardNavIndex === index + 2);

      if (isItemSelected) {
        addItemSelected(item, input);
      }
    });
  }

  if (e.key.includes('Left') || e.key.includes('Right')) {
    if (keyboardNavIndex !== -1) {
      isStopPropagation = true;
    }
  }

  if (isStopPropagation) {
    preventAllEvents(e);
  }
};

/**
 * Adds selected class to item
 * @type {import('./index').addItemSelected}
 */
const addItemSelected = (item, input) => {
  const idActiveDescendantFocus = 'boost-sd__suggestion-queries-item-selected';
  let textValue = item.getAttribute('data-title');

  if (
    item.classList.contains(EMBEDDED_BUNDLE_CLASS) ||
    item.classList.contains(VOLUME_BUNDLE_CLASS) ||
    item.classList.contains(BOTTOM_BUNDLE_CLASS)
  ) {
    textValue = '';
  }
  input.value = textValue || '';
  input.setAttribute('aria-activedescendant', idActiveDescendantFocus);
  item.id = idActiveDescendantFocus;
  item.classList.add('boost-sd__suggestion-queries-item-selected');
  item.setAttribute('aria-selected', 'true');
};

/**
 * Removes current selected item
 * @type {import('./index').removeCurrentSelected}
 */
const removeCurrentSelected = (context, input) => {
  const [getIsMobile] = context.useContextState('is-mobile', false);

  if (input) {
    input.removeAttribute('aria-activedescendant');
  }

  const suggestionSelector = context.document?.querySelector(
    `.boost-sd__instant-search-container-${getIsMobile() ? 'mobile' : 'desktop'} > div`
  );

  if (suggestionSelector) {
    const itemSelected = suggestionSelector?.querySelector(
      `.boost-sd__suggestion-queries-item-selected, [data-group="view-all-products"]`
    );

    if (itemSelected) {
      itemSelected.removeAttribute('id');
      itemSelected.classList.remove('boost-sd__suggestion-queries-item-selected');
      itemSelected.setAttribute('aria-selected', 'false');
    }
  }
};

/**
 * Selects current selected item
 * @type {import('./index').selectItemCurrentSelected}
 */
const selectItemCurrentSelected = (suggestionSelector) => {
  if (!isSuggestionOpening(suggestionSelector)) return;

  const selectElement = suggestionSelector?.querySelector(
    `boost-sd__suggestion-queries-item-selected > a`
  );
  if (selectElement) {
    const link = selectElement.getAttribute('href');
    if (link && !isBadUrl(link)) {
      return link;
    }
  }

  return false;
};

/**
 * Handles click outside the suggestion container
 * @type {import('./index').clickOutsideSuggestion}
 */
const clickOutsideSuggestion = (context, suggestionSelector, input, e) => {
  const hasMobileStyle2 = suggestionSelector?.querySelector(
    '.boost-sd__instant-search-results--mobile-style2'
  );

  // not apply style full width
  if ((isStyleFullWidth(context) && !hasMobileStyle2) || !input) return;

  let doClose = false;

  if (isSuggestionOpening(suggestionSelector) && e && e.target) {
    const activeElement = e.target;
    const isClickThisInput = activeElement?.id === input?.id;
    const isClickThisSearchButton =
      activeElement.closest('form') && activeElement.closest('[type="submit"]');
    const isClickSuggestion = activeElement.closest('.boost-sd__instant-search-container');
    const isClickHeaderIconSearch = activeElement.closest('.header__icon--search');
    const isClickWatermark = activeElement.closest('.boost-sd__watermark-powered-by-boost');
    const hasSearchModal = input.closest('.search-modal');

    if (
      !isClickThisInput &&
      !isClickThisSearchButton &&
      !isClickSuggestion &&
      !isClickHeaderIconSearch
    ) {
      doClose = true;
    }

    // if click Suggestion & theme not turn off hasSearchModal
    if (isClickSuggestion && hasSearchModal) {
      doClose = true;
    }

    // if click watermark and theme not turn off hasSearchModal then keep suggestion open
    if (isClickWatermark && !hasSearchModal) {
      doClose = false;
    }

    // if click button add bundle to cart in ISW then still close suggestion when hasSearchModal = true
    const isClickAddBundleToCartInPopupISW =
      isClickSuggestion && activeElement.closest('.boost-sd__recommendation-dynamic-show-popup');
    if (isClickAddBundleToCartInPopupISW && !doClose && hasSearchModal) {
      doClose = true;
    }
  } else if (!e) {
    doClose = true;
  }

  if (doClose) {
    onCloseSuggestion(context, input);
    document.body.classList.remove('boost-sd__search-opening');
  } else {
    if (!input.classList.contains('boost-sd__search-form-input')) {
      document.body.classList.add('boost-sd__search-opening');
    }
  }
};

/**
 * Transforms product price
 * @type {import('./index').transformProductPrice}
 */
const transformProductPrice = (context, products) => {
  const { showCentAsSuperscript, showCurrencyCodes } =
    context?.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};

  products?.forEach((productItem) => {
    const { price_min, compare_at_price_min } = productItem;

    productItem.price_min_format = formatCurrency({
      context,
      value: Number(price_min),
      showCurrencyCodes,
      showCentAsSuperscript,
      removeDecimalPoint: showCentAsSuperscript,
    });

    productItem.compare_at_price_min_format = formatCurrency({
      context,
      value: Number(compare_at_price_min),
      showCurrencyCodes,
      showCentAsSuperscript,
      removeDecimalPoint: showCentAsSuperscript,
    });

    productItem.sale_amount_format = formatCurrency({
      context,
      value:
        Number(compare_at_price_min) > Number(price_min)
          ? Number(compare_at_price_min) - Number(price_min)
          : 0,
      showCurrencyCodes: false,
      showCentAsSuperscript,
      removeDecimalPoint: showCentAsSuperscript,
    });
  });

  return products;
};
